/**
 * Shared diagram catalog helpers for @csk/demo-ui integrations.
 *
 * Keeps diagram-kind labeling and preview-path derivation outside individual
 * React components so apps can prepare diagram views consistently.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export const DIAGRAM_KIND_HEADINGS: Readonly<Record<string, string>> = {
  commutative_diagram: "Commutative diagrams",
  string_diagram: "String diagrams",
  tensor_network: "Tensor networks",
  hasse_lattice: "Hasse lattices",
  force_directed_trust_graph: "Force-directed trust graphs",
  linked_views_policy_comparison: "Linked-views policy comparisons",
  cytoscape_seam_explorer: "Cytoscape seam explorers",
};

export const DIAGRAM_KIND_ORDER = [
  "commutative_diagram",
  "string_diagram",
  "tensor_network",
  "hasse_lattice",
  "force_directed_trust_graph",
  "linked_views_policy_comparison",
  "cytoscape_seam_explorer",
] as const;

export type DiagramPreviewKind = "svg" | "html";

export interface DiagramSpecMeta {
  readonly id: string;
  readonly title?: string;
  readonly description?: string;
  readonly kind?: string;
  readonly csk_metadata?: {
    readonly theorems?: readonly string[];
    readonly color_role?: string;
  };
}

export interface DiagramGalleryItem {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly kind: string;
  readonly theorems: readonly string[];
  readonly previewKind: DiagramPreviewKind;
  readonly previewSource: string;
}

/**
 * Diagram-spec ids are interpolated into preview asset paths
 * (`<base>/<id>.<ext>`) that `DiagramGallery` renders as an `<iframe src>` /
 * `<img src>`. Constraining them to a single safe path segment keeps a
 * registry typo — or a hand-edited spec — from steering the gallery frame at
 * an absolute URL, a foreign origin, a parent directory, or a non-`http`
 * scheme. The character class excludes `/`, `\`, `:`, `?`, `#`, whitespace,
 * and control characters; the canonical numbered spec ids (`01-cit-...`)
 * already satisfy it.
 */
const SAFE_DIAGRAM_ID = /^[A-Za-z0-9._-]+$/;

export function normalizeDiagramAssetBasePath(assetBasePath: string): string {
  if (assetBasePath === "") return "";
  return assetBasePath.endsWith("/") ? assetBasePath.slice(0, -1) : assetBasePath;
}

export function resolveDiagramPreviewKind(kind: string | undefined): DiagramPreviewKind {
  return kind === "linked_views_policy_comparison" ? "html" : "svg";
}

export function diagramKindHeading(kind: string): string {
  return DIAGRAM_KIND_HEADINGS[kind] ?? kind;
}

export function diagramKindRank(kind: string): number {
  const index = DIAGRAM_KIND_ORDER.indexOf(kind as (typeof DIAGRAM_KIND_ORDER)[number]);
  return index >= 0 ? index : DIAGRAM_KIND_ORDER.length;
}

/** Convert diagram-spec JSON metadata into gallery items with derived preview paths. */
export function createDiagramGalleryItems(
  specs: readonly DiagramSpecMeta[],
  assetBasePath = "",
): readonly DiagramGalleryItem[] {
  const base = normalizeDiagramAssetBasePath(assetBasePath);
  return specs.map((spec) => {
    if (!SAFE_DIAGRAM_ID.test(spec.id)) {
      throw new Error(
        `createDiagramGalleryItems: diagram-spec id ${JSON.stringify(spec.id)} is not a ` +
          `safe path segment (expected /^[A-Za-z0-9._-]+$/). Ids are interpolated into ` +
          `preview asset URLs, so an unconstrained value could redirect the gallery frame.`,
      );
    }
    const previewKind = resolveDiagramPreviewKind(spec.kind);
    const previewSource = `${base === "" ? "" : `${base}/`}${spec.id}.${previewKind}`;
    const item: DiagramGalleryItem = {
      id: spec.id,
      title: spec.title ?? spec.id,
      kind: spec.kind ?? "unknown",
      theorems: spec.csk_metadata?.theorems ?? [],
      previewKind,
      previewSource,
    };
    if (spec.description !== undefined) {
      return { ...item, description: spec.description };
    }
    return item;
  });
}
